import { SOCKET_EVENT } from "./../common/utils/consts";
import { IGetOrdersInputs } from "./../common/models/api/IGetOrdersInputs";
import { IDisputeInputs } from "./../common/models/api/IDisputeInputs";
import { IApiMessage } from "./../common/models/api/IApiMessage";
import { IOrderCreateInputs } from "./../common/models/api/IOrderCreateInputs";
import { IAddress } from "./../common/models/order/IAddress";
import { IDispute } from "./../common/models/order/IDIspute";
import { IOrder, IOrdersResponse } from "../common/models/order/IOrder";
import { getFilterParameters } from "../common/utils/helpers";
import { api } from "./api";
import { ICancelReason } from "common/models/order/ICancelReason";
import { io, Socket } from "socket.io-client";
import { IRecentAddress } from "../common/models/order/IRecentAddress";

function createSocket(): Socket {
  return io("https://api.manipulyatr.ru", {
    auth: {
      token: localStorage.getItem("token") || "",
    },
    transports: ["websocket"],
  });
}

const orderApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllOrders: builder.query<
      IOrdersResponse[],
      Undefinable<IGetOrdersInputs>
    >({
      query(props) {
        return {
          url: `/orders${getFilterParameters([
            { paramName: "status_id[]", params: props?.statusId },
            {
              paramName: "vehicle_category_id[]",
              params: props?.vehicleCategoryId,
            },
          ])}`,
          params: {
            is_active: props?.isActive,
            company_id: props?.companyId,
            executor_company_id: props?.executorCompanyId,
            date_from: props?.dateFrom,
            date_to: props?.dateTo,
            user_id: props?.userId,
            payout_approved: props?.payoutApproved,
          },
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result
                .flatMap((d) => d.orders)
                .map(({ id }) => ({ type: "Order", id } as const)),
              { type: "Order", id: "LIST" },
              "Auth",
            ]
          : [{ type: "Order", id: "LIST" }],
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheEntryRemoved, cacheDataLoaded }
      ) {
        const socket = createSocket();
        await cacheDataLoaded;

        socket.emit("subscribe", { channel: "private-orders" });

        socket.on(
          SOCKET_EVENT.ORDER_UPDATED,
          (data: { payload: { order: IOrder } }) => {
            updateCachedData((draft) => {
              const order = data.payload.order;
              if (arg?.statusId) {
                if (arg.statusId.includes(order.status.id)) {
                  draft.forEach((d) => {
                    d.orders = d.orders.map((orderItem) =>
                      orderItem.id === order.id ? order : orderItem
                    );
                  });
                  return draft;
                } else {
                  draft.forEach((d) => {
                    d.orders = d.orders.filter(
                      (orderItem) => orderItem.id !== order.id
                    );
                  });
                  return draft;
                }
              } else {
                draft.forEach((d) => {
                  d.orders = d.orders.map((orderItem) =>
                    orderItem.id === order.id ? order : orderItem
                  );
                });
                return draft;
              }
            });
          }
        );

        socket.on(
          SOCKET_EVENT.ORDER_DELETED,
          (data: { payload: { order: IOrder } }) => {
            updateCachedData((draft) => {
              const order = data.payload.order;
              draft.forEach((d) => {
                d.orders = d.orders.filter(
                  (orderItem) => orderItem.id !== order.id
                );
              });
              return draft;
            });
          }
        );

        socket.on(
          SOCKET_EVENT.ORDER_CREATED,
          (data: { payload: { order: IOrder } }) => {
            updateCachedData((draft) => {
              const order = data.payload.order;
              if (arg?.statusId) {
                if (arg.statusId.includes(order.status.id)) {
                  return [
                    {
                      orders: [order],
                      ordersCount: 1,
                      date: "",
                    } as IOrdersResponse,
                    ...draft,
                  ];
                }
              } else {
                return [
                  {
                    orders: [order],
                    ordersCount: 1,
                    date: "",
                  } as IOrdersResponse,
                  ...draft,
                ];
              }
            });
          }
        );

        await cacheEntryRemoved;
        socket.emit("unsubscribe", { channel: "private-orders" });
        socket.disconnect();
      },
      transformResponse: (result: {
        data: { orders: IOrder[]; date: string; ordersCount: number }[];
      }) => result.data,
    }),
    getOrderById: builder.query<
      IOrder,
      {
        id: number;
      }
    >({
      query({ id }) {
        return {
          url: `/orders/${id}`,
        };
      },
      providesTags: (result, error, { id }) => [{ type: "Order", id }],
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheEntryRemoved, cacheDataLoaded }
      ) {
        const socket = createSocket();
        await cacheDataLoaded;

        socket.emit("subscribe", { channel: `private-orders.${arg.id}` });

        socket.on(
          SOCKET_EVENT.ORDER_ITEM_UPDATED,
          (data: { order: IOrder }) => {
            updateCachedData(() => data.order);
          }
        );

        await cacheEntryRemoved;
        socket.emit("unsubscribe", { channel: `private-orders.${arg.id}` });
        socket.disconnect();
      },
      transformResponse: (result: { order: IOrder }) => result.order,
    }),
    attachOrderVehicle: builder.mutation<
      IApiMessage,
      {
        order_id: number;
        vehicle_id: Nullable<number>;
        driver_id: Nullable<number>;
      }
    >({
      query({ order_id, ...body }) {
        return {
          url: `/orders/${order_id}/company/drivers/attach`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (result, error, { order_id }) =>
        !error?.status ? [{ type: "Order", id: order_id }] : [],
    }),
    attachOrderToCompany: builder.mutation<IApiMessage, number>({
      query(orderId) {
        return {
          url: `/orders/${orderId}/company/attach`,
          method: "POST",
        };
      },
      invalidatesTags: (result, error, orderId) =>
        !error?.status ? [{ type: "Order", id: orderId }] : [],
    }),
    detachOrderCompany: builder.mutation<IApiMessage, number>({
      query(orderId) {
        return {
          url: `/orders/${orderId}/company/detach`,
          method: "POST",
        };
      },
      invalidatesTags: (result, error, orderId) =>
        !error?.status ? [{ type: "Order", id: orderId }] : [],
    }),
    detachOrderVehicle: builder.mutation<
      IApiMessage,
      {
        orderId: number;
      }
    >({
      query({ orderId }) {
        return {
          url: `/orders/${orderId}/company/drivers/detach`,
          method: "POST",
        };
      },
      invalidatesTags: (result, error, { orderId }) =>
        !error?.status ? [{ type: "Order", id: orderId }] : [],
    }),
    createOrder: builder.mutation<IOrder, IOrderCreateInputs>({
      query(body) {
        return {
          url: `/orders`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (result, error) =>
        result ? [{ type: "Order", id: "LIST" }] : [],
      transformResponse: (result: { order: IOrder }) => result.order,
    }),
    confirmOrder: builder.mutation<IOrder, number>({
      query(orderId) {
        return {
          url: `/orders/${orderId}/confirm`,
          method: "POST",
        };
      },
      invalidatesTags: (result, error, arg) =>
        !error?.status ? [{ type: "Order", id: arg }] : [],
      transformResponse: (result: { orders: IOrder }) => result.orders,
    }),
    approveOrderPayout: builder.mutation<IOrder, number>({
      query(orderId) {
        return {
          url: `/orders/${orderId}/approve_payout`,
          method: "POST",
        };
      },
      invalidatesTags: (result, error, arg) =>
        !error?.status
          ? [
              { type: "Order", id: arg },
              { type: "Order", id: "LIST" },
            ]
          : [],
      transformResponse: (result: { order: IOrder }) => result.order,
    }),
    rejectOrderPayout: builder.mutation<
      IOrder,
      { id: number; reason?: string }
    >({
      query({ id, ...body }) {
        return {
          url: `/orders/${id}/reject_payout`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (result, error, { id }) =>
        !error?.status
          ? [
              { type: "Order", id },
              { type: "Order", id: "LIST" },
            ]
          : [],
      transformResponse: (result: { order: IOrder }) => result.order,
    }),
    cancelOrder: builder.mutation<IApiMessage, IDisputeInputs>({
      query({ order_id, ...body }) {
        return {
          url: `/orders/${order_id}/cancel`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (result, error, arg) =>
        !error?.status ? [{ type: "Order", id: arg.order_id }] : [],
    }),
    createDispute: builder.mutation<IDispute, IDisputeInputs>({
      query({ order_id, ...body }) {
        return {
          url: `/orders/${order_id}/disputes`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (result, error, orderId) =>
        !error?.status ? [{ type: "Order", id: "LIST" }] : [],
    }),
    updateOrder: builder.mutation<
      IOrder,
      {
        orderId: number;
        vehicleTypeId?: number;
        cityId?: number;
        options?: number[];
        searchRadiusId?: number;
        rate?: number;
      }
    >({
      query({ orderId, ...body }) {
        return {
          url: `/orders/${orderId}`,
          method: "PUT",
          body: {
            order_id: orderId,
            vehicle_type_id: body.vehicleTypeId,
            city_id: body.cityId,
            options: body.options,
            search_radius_id: body.searchRadiusId,
            rate: body.rate,
          },
        };
      },
      invalidatesTags: (result, error, { orderId }) =>
        !error?.status ? [{ type: "Order", id: orderId }] : [],
      transformResponse: (result: { order: IOrder }) => result.order,
    }),
    payOrder: builder.mutation<IApiMessage, number>({
      query(orderId) {
        return {
          url: `/orders/${orderId}/pay`,
          method: "POST",
        };
      },
      invalidatesTags: (result, error, id) =>
        !error?.status ? [{ type: "Order", id }] : [],
    }),
    uploadOrderFiles: builder.mutation<
      IOrder,
      {
        order_id: number;
        photos: number[];
      }
    >({
      query({ order_id, ...body }) {
        return {
          url: `/orders/${order_id}`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: (result, error, { order_id }) =>
        !error?.status ? [{ type: "Order", id: order_id }] : [],
      transformResponse: (result: { order: IOrder }) => result.order,
    }),
    getDisputes: builder.query<
      IDispute[],
      {
        dateFrom?: string;
        dateTo?: string;
        userId?: number;
        orderId?: number;
      }
    >({
      query({ userId, orderId, dateFrom, dateTo }) {
        return {
          url: `/disputes`,
          params: {
            user_id: userId,
            order_id: orderId,
            date_from: dateFrom,
            date_to: dateTo,
          },
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Order", id } as const)),
              { type: "Order", id: "LIST" },
            ]
          : [{ type: "Order", id: "LIST" }],
      transformResponse: (result: { disputes: IDispute[] }) => result.disputes,
    }),
    searchAddresses: builder.mutation<
      IAddress[],
      {
        city_id?: number;
        query: string;
      }
    >({
      query(body) {
        return {
          url: `/orders/search_address`,
          method: "POST",
          body,
        };
      },
      transformResponse: (result: { addresses: IAddress[] }) =>
        result.addresses,
    }),
    getRecentAddresses: builder.query<IRecentAddress[], void>({
      query() {
        return {
          url: `/orders/recent_addresses`,
        };
      },
      transformResponse: (result: { addresses: IRecentAddress[] }) =>
        result.addresses,
    }),
    getOrderCancelReasons: builder.query<ICancelReason[], void>({
      query() {
        return {
          url: `/cancel_reasons`,
        };
      },
      transformResponse: (result: { items: ICancelReason[] }) => result.items,
    }),
  }),
});

export const {
  useAttachOrderToCompanyMutation,
  useDetachOrderCompanyMutation,
  useDetachOrderVehicleMutation,
  useConfirmOrderMutation,
  useApproveOrderPayoutMutation,
  useRejectOrderPayoutMutation,
  useSearchAddressesMutation,
  useCreateOrderMutation,
  usePayOrderMutation,
  useUploadOrderFilesMutation,
  useGetAllOrdersQuery,
  useGetOrderByIdQuery,
  useAttachOrderVehicleMutation,
  useGetDisputesQuery,
  useCreateDisputeMutation,
  useCancelOrderMutation,
  useUpdateOrderMutation,
  useGetOrderCancelReasonsQuery,
  useGetRecentAddressesQuery,
} = orderApi;
